import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import {PRODUCTS} from '@/lib/constants';

/* ── Slide animation variants ── */
const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 48 : -48, opacity: 0, scale: 0.97 }),
  center: { x: 0, opacity: 1, scale: 1, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit:  (dir) => ({ x: dir > 0 ? -48 : 48, opacity: 0, scale: 0.97, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }),
};

/* ── Carousel card body (shared by visible + hidden measuring copies) ── */
function CarouselCardBody({ card }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      <div
        className="relative flex flex-col items-center justify-end min-h-[220px] lg:min-h-full overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, rgba(var(--c-purple-rgb),0.14) 0%, rgba(var(--c-purple-rgb),0.04) 100%)',
          borderBottom: '1px solid rgba(var(--c-purple-rgb),0.12)',
        }}
      >
        {card.image && (
          <img
            src={card.image}
            alt={card.title}
            className="absolute inset-0 w-full h-full object-contain"
          />
        )}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(10,10,20,0.1) 0%, rgba(10,10,20,0.85) 100%)' }}
        />
        <span
          className="relative z-10 pb-4 text-[10px] font-bold tracking-[0.18em] uppercase"
          style={{ color: 'var(--c-purple-400)' }}
        >
          {card.tag}
        </span>
      </div>

      {/* Right: content */}
      <div className="flex flex-col justify-center gap-5 p-8 md:p-10">
        <div>
          <h3 className="text-2xl md:text-3xl font-bold text-brand-white mb-3 tracking-tight">
            {card.title}
          </h3>
          <p className="text-sm text-brand-muted leading-relaxed">{card.desc}</p>
        </div>

        {card.bullets ? (
          <ul className="space-y-3">
            {card.bullets.map((b) => (
              <li key={b} className="flex items-center gap-3 text-sm text-brand-off-white">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: 'var(--c-purple-400)' }}
                />
                {b}
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-brand-muted">
              Every build is unique. Let's talk about what your business needs.
            </p>
            <Link
              to="/contact/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-blue text-white font-semibold text-sm self-start transition-all duration-300 shadow-[0_0_20px_rgba(var(--c-purple-rgb),0.3)] hover:shadow-[0_0_35px_rgba(var(--c-purple-rgb),0.55)] hover:bg-brand-blue-light"
            >
              Talk to Us <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Carousel component — cycles through every product ── */
export default function SolutionCarousel() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [direction, setDirection]  = useState(1);
  const [paused, setPaused]        = useState(false);
  const [stageHeight, setStageHeight] = useState(null);
  const measureRefs = useRef([]);
  const total = PRODUCTS.length;

  useEffect(() => {
    const measure = () => {
      const heights = measureRefs.current.map((el) => el?.offsetHeight || 0);
      setStageHeight(Math.max(...heights, 0));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setDirection(1);
      setActiveIdx(i => (i + 1) % total);
    }, 4200);
    return () => clearInterval(id);
  }, [paused, total]);

  const goTo = (idx) => {
    if (idx === activeIdx) return;
    setDirection(idx > activeIdx ? 1 : -1);
    setActiveIdx(idx);
  };

  const prev = () => { setDirection(-1); setActiveIdx(i => (i - 1 + total) % total); };
  const next = () => { setDirection(1);  setActiveIdx(i => (i + 1) % total); };

  const card = PRODUCTS[activeIdx];

  return (
    <div
      className="relative max-w-4xl mx-auto overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Hidden stack used only to measure the tallest card so the stage never resizes */}
      <div className="absolute inset-x-0 top-0 invisible pointer-events-none -z-10" aria-hidden="true">
        {PRODUCTS.map((c, i) => (
          <div key={c.title} ref={(el) => (measureRefs.current[i] = el)} className="glass-card overflow-hidden">
            <CarouselCardBody card={c} />
          </div>
        ))}
      </div>

      {/* Card stage */}
      <div
        className="relative overflow-hidden"
        style={{ height: stageHeight ? `${stageHeight}px` : undefined, minHeight: stageHeight ? undefined : 220 }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={activeIdx}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="glass-card overflow-hidden"
            data-testid={`solution-card-${card.title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <CarouselCardBody card={card} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation row */}
      <div className="flex items-center justify-center gap-5 mt-8">
        {/* Prev button */}
        <button
          onClick={prev}
          aria-label="Previous solution"
          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:opacity-80"
          style={{ background: 'rgba(var(--c-purple-rgb),0.10)', border: '1px solid rgba(var(--c-purple-rgb),0.25)' }}
        >
          <ChevronLeft size={18} className="text-brand-blue" />
        </button>

        {/* Dot indicators */}
        <div className="flex items-center gap-2">
          {PRODUCTS.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="rounded-full transition-all duration-300"
              style={{
                width:      i === activeIdx ? 24 : 8,
                height:     8,
                background: i === activeIdx ? 'var(--c-purple-500)' : 'rgba(var(--c-purple-rgb),0.28)',
              }}
            />
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={next}
          aria-label="Next solution"
          className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:opacity-80"
          style={{ background: 'rgba(var(--c-purple-rgb),0.10)', border: '1px solid rgba(var(--c-purple-rgb),0.25)' }}
        >
          <ChevronRight size={18} className="text-brand-blue" />
        </button>
      </div>
    </div>
  );
}
